import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  InputAdornment,
  Snackbar,
  Stack,
  SvgIcon,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus } from '../../../assets';
import PageTitle from '../../../components/shared/PageTitle';
import PageArea from '../../../components/shared/PageArea';
import BannersTable from './components/BannersTable';
import useBannerAdmin from './hooks/useBannerAdmin';
import { BannerSummary } from './types/BannerAdmin';
import useLogError from '../../../hooks/useLogError';
import { ConfirmationDeleteDialog } from '../../../components/shared/dialogs/ConfirmationDeleteDialog';

const BannersPage = () => {
  const { t } = useTranslation('banners');
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const navigate = useNavigate();
  const { logError } = useLogError();

  const {
    banners,
    totalCount,
    loading,
    fetchBanners,
    deleteBanner,
  } = useBannerAdmin();

  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [deletingBanner, setDeletingBanner] = useState<BannerSummary | undefined>(undefined);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const didMount = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (didMount.current) return;
    didMount.current = true;
    fetchBanners({ page, pageSize });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!didMount.current) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      fetchBanners({ search: searchQuery || undefined, page: 1, pageSize });
    }, 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
    fetchBanners({ search: searchQuery || undefined, page: newPage, pageSize });
  }, [fetchBanners, searchQuery]);

  const handleCreate = () => {
    navigate('/settings/content-management/banners/create');
  };

  const handleEdit = useCallback((summary: BannerSummary) => {
    navigate(`/settings/content-management/banners/edit/${summary.id}`);
  }, [navigate]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deletingBanner) return;
    try {
      await deleteBanner(deletingBanner.id);
      setSnackbar({ open: true, message: t('deleteSuccess'), severity: 'success' });
      setDeletingBanner(undefined);
      fetchBanners({ search: searchQuery || undefined, page, pageSize });
    } catch (error) {
      logError(error);
      setSnackbar({ open: true, message: t('deleteError'), severity: 'error' });
    }
  }, [deletingBanner, deleteBanner, fetchBanners, logError, t, searchQuery, page]);

  return (
    <PageArea>
      <Stack
        width="100%"
        spacing={2}
        direction={isMobile ? 'column' : 'row'}
        sx={{ justifyContent: 'space-between' }}
      >
        <Box width={isMobile ? '100%' : 'auto'}>
          <PageTitle subTitle={t('bannersSubtitle')}>
            {t('banners')}
          </PageTitle>
        </Box>

        <Box
          width={isMobile ? '100%' : 'auto'}
          sx={{
            display: 'flex',
            justifyContent: { xs: 'flex-start', md: 'flex-end' },
          }}
        >
          <Button
            variant="primary"
            size="lg"
            startIcon={
              <SvgIcon>
                <Plus fill="white" />
              </SvgIcon>
            }
            onClick={handleCreate}
          >
            {t('createBanner')}
          </Button>
        </Box>
      </Stack>

      <TextField
        placeholder={t('searchBanners')}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        size="small"
        sx={{ mt: 2, maxWidth: 360 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 20, color: theme.palette.custom.gray[400] }} />
            </InputAdornment>
          ),
        }}
      />

      <BannersTable
        banners={banners}
        loading={loading}
        searchQuery={searchQuery}
        totalCount={totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onEdit={handleEdit}
        onDelete={(banner) => setDeletingBanner(banner)}
        onCreate={handleCreate}
      />

      <ConfirmationDeleteDialog
        open={Boolean(deletingBanner)}
        onCancel={() => setDeletingBanner(undefined)}
        onConfirm={handleDeleteConfirm}
        nameOfObject={deletingBanner?.title ?? ''}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageArea>
  );
};

export default BannersPage;
